/**
 * Shared display formatting for the cash-receipt detail sections. USD is
 * hardcoded to match the rest of the cash-receipt surfaces (amounts are
 * ingested without a currency and treated as USD throughout).
 */

import type { useGT } from 'gt-react';
import { formatDateOnlyString } from '@/utils/dateUtils';

export function formatAmount(amount: string | null): string {
  if (amount === null) return '—';
  const num = parseFloat(amount);
  if (isNaN(num)) return amount;
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
  }).format(num);
}

export function formatDate(dateStr: string | null): string {
  if (!dateStr) return '—';
  // Date-only strings (paymentDate, statedPaymentDate) must not be parsed as
  // UTC midnight — that renders the previous day in US timezones. The shared
  // helper parses those locally and only treats full timestamps as instants.
  return formatDateOnlyString(dateStr, '—');
}

type Translate = ReturnType<typeof useGT>;

/**
 * Display label for a receipt's payment type. Famous receipts carry a decoded
 * BANK_DEPOSIT_LINE.DEPOSITTYPE (check / eft / cash / internal); email and
 * Plaid rows carry the bank's own channel (ach, wire, online, …). Unknown
 * values fall through as-is rather than being hidden.
 */
export function formatPaymentType(
  type: string | null,
  gt: Translate,
): string | null {
  if (!type) return null;
  const context = 'cash receipt payment type';
  switch (type.trim().toLowerCase()) {
    case 'check':
      return gt('Check', { context });
    case 'eft':
      return gt('EFT', { context });
    case 'ach':
      return gt('ACH', { context });
    case 'wire':
      return gt('Wire', { context });
    case 'cash':
      return gt('Cash', { context });
    case 'internal':
      return gt('Internal adjustment', { context });
    default:
      return type;
  }
}
